import React, { useState, useEffect, useCallback } from 'react';
import {
  Radar as RadarIcon, RefreshCw, AlertCircle, AlertTriangle, Info,
  ChevronDown, ChevronRight, ExternalLink, EyeOff, Check,
  Loader, RotateCcw, Inbox, History,
  Activity, Sparkles, Wallet, TrendingUp, Search, Gavel, Package, Layers,
  Wrench, Lock
} from 'lucide-react';
import './RadarView.css';
import ModaleCorrection from './ModaleCorrection';
import JournalCorrections from './JournalCorrections';
import TexteAccentue from './TexteAccentue';
import { elementActionnable } from './elementActionnable';
import { colonnesDesElements } from './colonnesElements';
import { libellePlateforme } from '../constants/plateformes';
import googleAdsLogo from '../logos/google-ads.png';
import googleMerchantLogo from '../logos/google-merchant.png';
import googleSearchConsoleLogo from '../logos/google-search-console.png';

/**
 * Radar — le feed des actions à entreprendre sur les comptes connectés.
 *
 * Une carte par recommandation, triée par gravité, tous comptes confondus. Chaque carte se lit,
 * se traite ou s'ignore, et l'on sait qui a fait quoi : c'est ce que l'accueil ne permettait pas,
 * où l'on ne lisait que des scores.
 *
 * ## Pas de raccourci promis quand il ne peut pas tenir
 *
 * Ouvrir un compte depuis un lien exige son `ocid`, un identifiant interne de l'interface Google
 * Ads que l'API n'expose pas — vérifié : la ressource `customer` ne rend que `resource_name`, `id`
 * et `descriptive_name`, et l'interface n'emploie aucune autre forme dans ses propres liens. Il
 * n'existe donc aucun moyen de le deviner.
 *
 * Cet écran n'en réclame pas pour autant. Un bandeau « N comptes n'ont pas de lien » reportait sur
 * le lecteur une limite technique dont il n'est pas responsable et qu'il ne peut lever qu'en
 * copiant vingt URL à la main. Les comptes dont l'`ocid` est renseigné dans la bibliothèque
 * gagnent leurs raccourcis ; les autres n'affichent simplement pas d'icône.
 *
 * Les couleurs et les seuils sont ceux du diagnostic — il s'agit des mêmes constats, présentés
 * autrement ; deux palettes divergentes diraient au lecteur qu'il regarde deux choses.
 *
 * ## Une seule échelle de couleur par carte : la gravité
 *
 * La carte a d'abord porté deux échelles — le liseré disait le statut du constat (rouge ou orange),
 * le badge disait la gravité du contrôle. Elles se contredisaient à l'œil : un « À surveiller »
 * bordé de rouge se lisait comme un « Critique », un « Information » bordé d'orange comme une
 * alerte. Tout ce qui est coloré sur une carte dérive donc maintenant de sa gravité, et d'elle
 * seule.
 *
 * Le statut n'est pas perdu pour autant : il règle l'**intensité** du badge, dans la teinte de la
 * gravité. Un constat rouge se remplit, un orange reste en aplat clair. On distingue donc toujours
 * le pire de chaque famille sans introduire une seconde couleur qui dirait le contraire de la
 * première.
 */


/**
 * Le logo d'une plateforme, indexé par le `type` du compte.
 *
 * Il l'était par `tool` : deux entrées y rendaient le même logo Google Ads — le Diagnostic et
 * les recommandations d'optimisation portent sur la même régie — et une troisième source sur une
 * plateforme déjà connue aurait réclamé une ligne de plus. Le type du compte est la question
 * posée, et le serveur le rend maintenant sur chaque ligne (`plateforme`).
 *
 * **Aucun repli.** Il affichait le logo Google Ads, donc une plateforme oubliée ici mentait en
 * silence sur l'origine du constat. Faute de logo, la carte n'en montre pas : l'absence se voit,
 * la fausse attribution non. Le libellé, lui, vient de `libellePlateforme` et n'est jamais muet.
 */
const LOGOS = {
  GOOGLE_ADS: googleAdsLogo,
  GOOGLE_MERCHANT_API: googleMerchantLogo,
  GOOGLE_SEARCH_CONSOLE: googleSearchConsoleLogo
};

const LIBELLES_SEVERITE = {
  CRITICAL: { texte: 'Critique', icone: AlertCircle, couleur: '#ef4444' },
  WARNING: { texte: 'À surveiller', icone: AlertTriangle, couleur: '#f59e0b' },
  INFO: { texte: 'Information', icone: Info, couleur: '#3b82f6' }
};

/**
 * Au-delà de ce délai, un constat n'est plus une liste de travail mais une photo.
 *
 * Le diagnostic passe chaque nuit : trois jours laissent donc la place à une nuit ratée sans
 * crier au loup, tout en attrapant le cas qui trompe vraiment — un compte dont le scan échoue
 * depuis des semaines, et dont Radar continuait d'afficher les constats comme s'ils dataient
 * d'hier. Sur un tel compte, les campagnes ont pu être mises en pause, renommées ou supprimées
 * entre-temps : les contrôles filtrent bien ce qui est actif, mais au jour du scan.
 */
const PEREMPTION_JOURS = 3;

/** L'âge d'un relevé, dit en clair. `null` s'il est frais. */
const ageDuConstat = (date) => {
  if (!date) return null;
  const jours = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
  if (jours < PEREMPTION_JOURS) return null;
  if (jours < 31) return `relevé il y a ${jours} jours`;
  const mois = Math.round(jours / 30);
  return `relevé il y a ${mois} mois`;
};

/**
 * Les familles d'actions, dans l'ordre où l'on veut les traiter.
 *
 * Cet ordre n'est pas alphabétique et ne suit pas celui de la base : il va du socle vers le
 * réglage fin. Un tracking faux rend toute lecture de performance douteuse, et une erreur
 * d'hygiène fait dépenser pour rien — les corriger d'abord change le sens des familles suivantes,
 * alors que l'inverse n'est pas vrai.
 *
 * ## Ces pastilles ne sont pas colorées, et c'est voulu
 *
 * Sur cet écran, **la couleur dit la gravité** et rien d'autre (voir l'en-tête du fichier). Donner
 * une teinte à chaque famille installerait une seconde échelle qui se lirait comme la première :
 * un « Budget » vert passerait pour une bonne nouvelle. Les familles se distinguent donc par leur
 * icône et leur libellé, la pastille allumée se contentant du gris d'encre de l'écran.
 */
const FAMILLES = [
  { cle: 'tracking', label: 'Tracking', icone: Activity },
  { cle: 'hygiene', label: 'Hygiène du compte', icone: Sparkles },
  { cle: 'budget', label: 'Budget', icone: Wallet },
  { cle: 'performance', label: 'Performances', icone: TrendingUp },
  { cle: 'mots-cles', label: 'Mots-clés', icone: Search },
  { cle: 'encheres', label: 'Enchères', icone: Gavel },
  { cle: 'flux', label: 'Flux produits', icone: Package }
];

const PAR_FAMILLE = new Map(FAMILLES.map(f => [f.cle, f]));

/**
 * L'étiquette d'une famille.
 *
 * Les familles viennent de `DiagnosticCheckType.family` : une famille ajoutée en base n'est pas
 * connue d'ici, et le serveur la rend quand même. Son identifiant brut s'affiche alors tel quel —
 * laid, mais exact, et visible par celui qui vient de l'ajouter. Le taire la ferait disparaître du
 * filtre sans que rien ne le signale.
 */
const familleConnue = (cle) => PAR_FAMILLE.get(cle) || { cle, label: cle, icone: Layers };

/**
 * Ce qu'on affiche quand le corps de la réponse n'est pas du JSON : le code HTTP, et rien d'autre.
 *
 * Ces codes-là ne viennent pas de nous. Une passerelle les pose quand le backend ne répond pas, et
 * elle y joint sa propre page — du texte, du HTML, jamais notre enveloppe `{ success, error }`.
 */
const MESSAGES_HTTP = {
  502: "Le serveur n'a pas répondu (502). Il redémarre peut-être : réessayez dans un instant.",
  503: "Le serveur n'est pas disponible (503). Il redémarre peut-être : réessayez dans un instant.",
  504: "Le serveur n'a pas répondu (504). Il redémarre peut-être : réessayez dans un instant."
};

/**
 * Lit une réponse d'API **sans supposer qu'elle porte du JSON**.
 *
 * Analyser directement le corps en JSON convient tant que c'est notre backend qui répond. Cela ne
 * convient plus dès que quelqu'un répond à sa place : le proxy de développement rend « Error
 * occurred while trying to proxy… » en texte brut quand le backend est arrêté, et l'écran
 * affichait alors « Unexpected token 'E', "Error occu"… is not valid JSON » — un message qui parle
 * de syntaxe là où le seul fait est que le serveur n'a pas répondu. Vécu : 504 sur
 * `/api/radar/feed` pendant un redémarrage du backend, et un lecteur envoyé chercher la panne dans
 * le feed.
 *
 * Le corps est donc lu en texte puis interprété. Faute de JSON, c'est le **code HTTP** qui nomme
 * la panne — lui seul est fiable dans ce cas.
 */
const lireReponse = async (reponse) => {
  const texte = await reponse.text();
  try {
    return JSON.parse(texte);
  } catch {
    if (reponse.ok) throw new Error('Réponse illisible du serveur.');
    throw new Error(MESSAGES_HTTP[reponse.status] || `Le serveur a répondu ${reponse.status}.`);
  }
};

/**
 * @param {string}   selectedSiteId         le site choisi dans la barre latérale, ou `'all'`
 * @param {string}   plateformeExterne      la plateforme choisie dans la barre latérale
 * @param {Function} onPlateformesConnues   remonte les plateformes **présentes dans le feed**,
 *   pour que la barre latérale n'en propose aucune qui viderait la liste
 * @param {Function} onPlateformeChoisie    remonte un clic de pastille, pour que la barre
 *   latérale reflète ce que montre le feed
 */
const RadarView = ({
  token,
  selectedClientId,
  selectedSiteId,
  plateformeExterne,
  onPlateformesConnues,
  onPlateformeChoisie
}) => {
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recommandations, setRecommandations] = useState([]);
  const [repartition, setRepartition] = useState({ CRITICAL: 0, WARNING: 0, INFO: 0 });
  const [famillesPresentes, setFamillesPresentes] = useState({});
  const [plateformesPresentes, setPlateformesPresentes] = useState({});
  const [comptes, setComptes] = useState([]);
  const [scannesLe, setScannesLe] = useState(null);

  const [vue, setVue] = useState('actives');
  const [filtreCompte, setFiltreCompte] = useState('');
  const [filtreSeverite, setFiltreSeverite] = useState('');
  const [filtreFamille, setFiltreFamille] = useState('');
  const [filtrePlateforme, setFiltrePlateforme] = useState('');
  const [depliees, setDepliees] = useState({});
  const [enCours, setEnCours] = useState({});
  /** La correction ouverte, s'il y en a une : `{ reco, action }`. */
  const [correction, setCorrection] = useState(null);
  const [journalOuvert, setJournalOuvert] = useState(false);
  /** Les relevés en cours, par recommandation : un rejeu peut durer une minute. */
  const [relances, setRelances] = useState({});

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const parametres = new URLSearchParams({ vue });
      if (selectedClientId) parametres.set('clientId', selectedClientId);
      // Le site vient de la barre latérale ; `'all'` ne borne rien.
      if (selectedSiteId && selectedSiteId !== 'all') parametres.set('siteId', selectedSiteId);
      if (filtreCompte) parametres.set('accountId', filtreCompte);
      if (filtreSeverite) parametres.set('severite', filtreSeverite);
      if (filtreFamille) parametres.set('famille', filtreFamille);
      if (filtrePlateforme) parametres.set('plateforme', filtrePlateforme);

      const reponse = await fetch(`/api/radar/feed?${parametres}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const donnees = await lireReponse(reponse);

      if (!reponse.ok || !donnees.success) {
        throw new Error(donnees.error || 'Chargement impossible');
      }

      setRecommandations(donnees.recommandations || []);
      setRepartition(donnees.repartition || { CRITICAL: 0, WARNING: 0, INFO: 0 });
      setFamillesPresentes(donnees.familles || {});
      setPlateformesPresentes(donnees.plateformes || {});
      setComptes(donnees.comptes || []);
      setScannesLe(donnees.scannesLe || null);

      /*
       * Les plateformes réellement présentes remontent à la barre latérale.
       *
       * ⚠️ Sans cela elle proposerait les plateformes **connectées** — sept sur ce parc — alors
       * que le feed n'en porte que deux : cinq entrées videraient la liste au clic, ce qui se lit
       * comme une panne et non comme un filtre.
       */
      if (onPlateformesConnues) onPlateformesConnues(donnees.plateformes || {});
    } catch (e) {
      setErreur(e.message);
      setRecommandations([]);
      setRepartition({ CRITICAL: 0, WARNING: 0, INFO: 0 });
      setFamillesPresentes({});
      setPlateformesPresentes({});
      /*
       * La date du relevé décrit le feed qu'on vient de ne pas obtenir : la garder faisait
       * cohabiter « relevé le 2 octobre » et un bandeau de péremption avec un message d'erreur,
       * sur un écran qui ne savait plus rien. Les comptes, eux, restent : c'est une commande de
       * filtre, pas une affirmation sur le feed, et l'effacer retirerait au lecteur le moyen de
       * relancer sur un compte précis.
       */
      setScannesLe(null);
    } finally {
      setChargement(false);
    }
  }, [token, vue, selectedClientId, selectedSiteId, filtreCompte, filtreSeverite, filtreFamille,
    filtrePlateforme, onPlateformesConnues]);

  useEffect(() => { charger(); }, [charger]);

  /**
   * Enregistre une décision puis retire la carte de la liste sans recharger.
   *
   * Le retrait local plutôt qu'un rechargement : la liste compte souvent deux cents lignes, et
   * les rejouer toutes ferait sauter la position de lecture à chaque geste.
   */
  const decider = async (reco, decision) => {
    setEnCours(precedent => ({ ...precedent, [reco.id]: true }));
    try {
      const reponse = await fetch('/api/radar/decision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          tool: reco.tool,
          accountId: reco.accountId,
          checkKey: reco.checkKey,
          statusObserve: reco.status,
          decision
        })
      });
      const donnees = await lireReponse(reponse);
      if (!reponse.ok || !donnees.success) throw new Error(donnees.error || 'Échec');

      setRecommandations(liste => liste.filter(r => r.id !== reco.id));
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnCours(precedent => ({ ...precedent, [reco.id]: false }));
    }
  };

  /**
   * Une correction a abouti : la carte quitte la liste.
   *
   * Le serveur a déjà consigné la décision et rejoué le contrôle — la ligne n'a plus d'objet.
   * Retrait local plutôt que rechargement, comme pour les décisions : rejouer deux cents lignes
   * ferait sauter la position de lecture au moment même où l'on vient d'agir.
   */
  const corrige = (reco) => {
    setRecommandations(liste => liste.filter(r => r.id !== reco.id));
  };

  /**
   * Relance le relevé de **ce seul contrôle**, et met la carte à jour sur place.
   *
   * Un contrôle prend une seconde, là où un scan complet en prend plusieurs minutes. Les autres
   * cartes du même compte gardent donc leur âge : on rafraîchit ce qu'on regarde.
   *
   * ⚠️ La carte est mise à jour localement plutôt que de recharger le feed : rejouer deux cents
   * lignes ferait sauter la position de lecture au moment même où l'on examine une carte.
   */
  const relancer = async (reco) => {
    setEnCours(precedent => ({ ...precedent, [reco.id]: true }));
    setRelances(precedent => ({ ...precedent, [reco.id]: true }));
    setErreur(null);
    try {
      const reponse = await fetch('/api/actions/rejouer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tool: reco.tool, accountId: reco.accountId, checkKey: reco.checkKey })
      });
      const donnees = await lireReponse(reponse);
      if (!reponse.ok || !donnees.success) throw new Error(donnees.error || 'Échec');

      const constat = donnees.constat;
      // Le problème a disparu : la carte n'a plus d'objet.
      if (constat.status === 'GREEN' || constat.status === 'SKIPPED') {
        setRecommandations(liste => liste.filter(r => r.id !== reco.id));
        return;
      }

      /*
       * Le constat tient toujours : on remplace ce qui a changé. Les actions sont recalculées
       * par le serveur au prochain chargement du feed — ici on garde les anciennes, dont les
       * cibles viennent du constat qu'on vient de remplacer. Recharger la carte seule
       * demanderait une route de plus pour un gain nul : l'aperçu relit de toute façon l'état
       * réel avant d'écrire.
       */
      setRecommandations(liste => liste.map(r => (r.id === reco.id ? {
        ...r,
        status: constat.status,
        summary: constat.detail?.summary || r.summary,
        affectedItems: constat.detail?.affectedItems || r.affectedItems,
        affectedCount: constat.detail?.affectedCount ?? r.affectedCount,
        affectedComplet: constat.detail?.affectedComplet ?? r.affectedComplet,
        rejoueLe: new Date().toISOString()
      } : r)));
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnCours(precedent => ({ ...precedent, [reco.id]: false }));
      setRelances(precedent => ({ ...precedent, [reco.id]: false }));
    }
  };

  /** Revenir sur une décision : la recommandation retourne dans les actives. */
  const reactiver = async (reco) => {
    if (!reco.decision?.id) return;
    setEnCours(precedent => ({ ...precedent, [reco.id]: true }));
    try {
      const reponse = await fetch(`/api/radar/decision/${reco.decision.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const donnees = await lireReponse(reponse);
      if (!reponse.ok || !donnees.success) throw new Error(donnees.error || 'Échec');

      setRecommandations(liste => liste.filter(r => r.id !== reco.id));
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnCours(precedent => ({ ...precedent, [reco.id]: false }));
    }
  };

  /**
   * Bascule le filtre de gravité. Cliquer la gravité déjà choisie la relâche : c'est le même
   * geste qui pose et retire, sans qu'il faille retrouver « Toutes gravités » dans la liste.
   */
  const basculerSeverite = (cle) => setFiltreSeverite(actuel => (actuel === cle ? '' : cle));

  /*
   * La barre latérale et les pastilles pilotent **le même** filtre de plateforme : choisir dans
   * l'une allume l'autre. Deux commandes de même nom qui divergeraient laisseraient l'écran
   * mentir sur son propre état — on relâcherait une pastille éteinte sans que rien ne change.
   */
  useEffect(() => {
    if (plateformeExterne === undefined) return;
    const voulu = plateformeExterne || '';
    setFiltrePlateforme(actuel => (actuel === voulu ? actuel : voulu));
  }, [plateformeExterne]);

  /** Même geste pour les familles : la pastille allumée se relâche d'un second clic. */
  const basculerFamille = (cle) => setFiltreFamille(actuel => (actuel === cle ? '' : cle));

  /**
   * Même geste pour les plateformes, à une règle près : **le compte choisi la suit**.
   *
   * Un compte Merchant retenu sous un filtre « Google Ads » ne rendrait aucune carte, et la liste
   * déroulante — bornée elle aussi à la plateforme — ne le montrerait même plus : on lirait
   * « Tous les comptes » au-dessus d'un écran vide, sans rien à relâcher. Le filtre de compte est
   * donc levé quand il devient incompatible.
   */
  /*
   * ⚠️ **Le calcul du prochain état ne porte aucun effet de bord**, et ce n'est pas une question
   * de style : React exécute la fonction passée à un `set…` **pendant le rendu**, et peut la
   * rejouer. Les deux gestes qui l'accompagnent y vivaient — lever le filtre de compte, et
   * remonter le choix à la barre latérale. Le second écrivait donc dans `App` au milieu du rendu
   * de `RadarView`, ce que React signale : « Cannot update a component (App) while rendering a
   * different component (RadarView) ». Le prochain état se calcule ici à partir de l'état du
   * rendu courant — nous sommes dans un gestionnaire de clic, il est à jour — et les trois
   * écritures sont posées côte à côte, hors de tout calcul.
   */
  const basculerPlateforme = (cle) => {
    const suivant = filtrePlateforme === cle ? '' : cle;
    if (suivant && filtreCompte) {
      const choisi = comptes.find(c => c.accountId === filtreCompte);
      if (choisi && choisi.type !== suivant) setFiltreCompte('');
    }
    setFiltrePlateforme(suivant);
    // Remonter le choix : la barre latérale doit refléter ce que montre le feed.
    if (onPlateformeChoisie) onPlateformeChoisie(suivant || null);
  };

  /**
   * Les familles à afficher : celles que le serveur a vues, dans l'ordre de `FAMILLES`, puis
   * celles qu'il a rendues sans qu'on les connaisse — à la fin, mais présentes.
   *
   * Une famille absente du feed n'a pas de pastille : une pastille à zéro ne mènerait nulle part,
   * et sept boutons morts en tête d'écran feraient passer un parc sain pour une liste tronquée.
   * Seule exception, posée par le serveur : la famille déjà choisie reste là même à zéro, faute de
   * quoi on ne pourrait plus la relâcher.
   */
  const famillesParOrdre = [
    ...FAMILLES.filter(f => famillesPresentes[f.cle] !== undefined),
    ...Object.keys(famillesPresentes)
      .filter(cle => !PAR_FAMILLE.has(cle))
      .sort()
      .map(familleConnue)
  ].map(f => ({ ...f, nombre: famillesPresentes[f.cle] }));

  /** Les comptes que la plateforme retenue laisse passer : ceux de la liste déroulante. */
  const comptesVisibles = filtrePlateforme
    ? comptes.filter(c => c.type === filtrePlateforme)
    : comptes;

  /**
   * Les plateformes à afficher, par ordre alphabétique de libellé.
   *
   * Pas d'ordre déclaré ici, contrairement aux familles : celles-là vont du socle vers le réglage
   * fin, alors qu'aucune régie ne se traite avant une autre. L'alphabet a le mérite de ne pas
   * déplacer une pastille sous le curseur quand un décompte change.
   *
   * La liste vient du serveur, qui la tire du `type` des comptes : une plateforme nouvelle
   * apparaît d'elle-même, et une plateforme absente du feed n'a pas de pastille — elle ne mènerait
   * nulle part.
   */
  const plateformesParOrdre = Object.entries(plateformesPresentes)
    .map(([cle, nombre]) => ({ cle, label: libellePlateforme(cle), nombre }))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));

  const formaterDate = (valeur) => {
    if (!valeur) return null;
    return new Date(valeur).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="radar-view">
      <div className="radar-entete">
        <div className="radar-titre">
          <RadarIcon size={26} />
          <div>
            <h1>Radar</h1>
            <p>
              Les actions à entreprendre sur vos comptes connectés
              {scannesLe && ` — relevé le ${formaterDate(scannesLe)}`}
            </p>
          </div>
        </div>
        <div className="radar-entete-boutons">
          {/* Ce qui a été écrit dans les régies depuis Kokpit, et comment le défaire. Une
              écriture ne doit pas être visible seulement le temps d'une modale. */}
          <button
            className="radar-journal-bouton"
            onClick={() => setJournalOuvert(true)}
            title="Les corrections écrites dans les régies depuis Kokpit"
          >
            <History size={15} />
            Corrections
          </button>
          <button className="radar-bouton-actualiser" onClick={charger} disabled={chargement}>
            <RefreshCw size={16} className={chargement ? 'radar-tourne' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      <div className="radar-filtres">
        <div className="radar-onglets">
          <button
            className={vue === 'actives' ? 'actif' : ''}
            onClick={() => setVue('actives')}
          >
            À traiter
          </button>
          <button
            className={vue === 'ignorees' ? 'actif' : ''}
            onClick={() => setVue('ignorees')}
          >
            <EyeOff size={14} />
            Recos traitées ou ignorées
          </button>
        </div>

        <div className="radar-selecteurs">
          {/* Bornée à la plateforme retenue : proposer un compte Merchant sous un filtre
              « Google Ads » reviendrait à offrir un couple qui ne rend aucune carte. */}
          <select value={filtreCompte} onChange={e => setFiltreCompte(e.target.value)}>
            <option value="">Tous les comptes ({comptesVisibles.length})</option>
            {comptesVisibles.map(c => (
              <option key={c.libraryItemId} value={c.accountId}>{c.name}</option>
            ))}
          </select>
          <select value={filtreSeverite} onChange={e => setFiltreSeverite(e.target.value)}>
            <option value="">Toutes gravités</option>
            <option value="CRITICAL">Critique</option>
            <option value="WARNING">À surveiller</option>
            <option value="INFO">Information</option>
          </select>
        </div>
      </div>

      {/* Les trois familles de filtres sur une seule rangée : gravité, plateforme, famille.
          Elles étaient empilées, ce qui prenait trois lignes pour une poignée de pastilles et
          éloignait d'autant le premier constat. Le conteneur se replie de lui-même quand la
          place manque, sans qu'on ait à décider où couper. */}
      {!chargement && (
        <div className="radar-filtres-puces">
        <div className="radar-repartition">
          {Object.entries(LIBELLES_SEVERITE).map(([cle, meta]) => {
            // Les trois restent affichées même à zéro dès qu'un filtre est posé : ce sont elles
            // qui permettent d'en changer ou d'en sortir.
            if (repartition[cle] === 0 && !filtreSeverite) return null;
            const active = filtreSeverite === cle;
            return (
              <button
                key={cle}
                type="button"
                className={`radar-puce${active ? ' active' : ''}`}
                style={active
                  ? { borderColor: meta.couleur, background: meta.couleur, color: '#fff' }
                  : { borderColor: meta.couleur, color: meta.couleur }}
                onClick={() => basculerSeverite(cle)}
                title={active ? 'Retirer ce filtre' : `Ne montrer que « ${meta.texte} »`}
                aria-pressed={active}
              >
                <meta.icone size={14} />
                {repartition[cle]} {meta.texte.toLowerCase()}
              </button>
            );
          })}
        </div>

      {/* Avant les familles : la plateforme est l'axe le plus large — on choisit la régie, puis
          ce qu'on y traite. Une seule plateforme au feed ne fait pas une rangée : un unique bouton
          qui ne retire rien n'ouvre aucun chemin. Sauf si c'est lui qui filtre — une famille
          propre à une régie peut réduire la liste à cette seule plateforme, et la rangée doit
          rester là pour qu'on puisse la relâcher. */}
      {(plateformesParOrdre.length > 1 || filtrePlateforme) && (
        <div className="radar-repartition radar-plateformes">
          {plateformesParOrdre.map(({ cle, label, nombre }) => {
            const active = filtrePlateforme === cle;
            return (
              <button
                key={cle}
                type="button"
                className={`radar-puce radar-puce-famille radar-puce-plateforme${active ? ' active' : ''}`}
                onClick={() => basculerPlateforme(cle)}
                title={active ? 'Retirer ce filtre' : `Ne montrer que « ${label} »`}
                aria-pressed={active}
              >
                {LOGOS[cle]
                  ? <img src={LOGOS[cle]} alt="" />
                  : <Layers size={14} />}
                {label}
                <span className="radar-puce-nombre">{nombre}</span>
              </button>
            );
          })}
        </div>
      )}

      {famillesParOrdre.length > 0 && (
        <div className="radar-repartition radar-familles">
          {famillesParOrdre.map(({ cle, label, icone: Icone, nombre }) => {
            const active = filtreFamille === cle;
            return (
              <button
                key={cle}
                type="button"
                className={`radar-puce radar-puce-famille${active ? ' active' : ''}`}
                onClick={() => basculerFamille(cle)}
                title={active ? 'Retirer ce filtre' : `Ne montrer que « ${label} »`}
                aria-pressed={active}
              >
                <Icone size={14} />
                {label}
                <span className="radar-puce-nombre">{nombre}</span>
              </button>
            );
          })}
        </div>
      )}
        </div>
      )}

      {!chargement && ageDuConstat(scannesLe) && (
        <div className="radar-avis radar-avis-alerte">
          <History size={15} />
          <span>
            Le plus ancien relevé date de plus de {PEREMPTION_JOURS} jours. Les comptes ont pu
            changer depuis — campagnes mises en pause, catalogue mis à jour : vérifiez le relevé
            des comptes concernés avant d'agir.
          </span>
        </div>
      )}

      {erreur && (
        <div className="radar-erreur">
          <AlertCircle size={18} />
          {erreur}
        </div>
      )}

      {chargement ? (
        <div className="radar-vide">
          <Loader size={28} className="radar-tourne" />
          <p>Chargement du feed…</p>
        </div>
      ) : erreur ? (
        /* Après un échec, aucun état vide : « rien à traiter » affirmerait un parc sain alors
           que rien n'a été lu. Le bloc d'erreur ci-dessus dit seul ce qui s'est passé. */
        null
      ) : recommandations.length === 0 ? (
        <div className="radar-vide">
          <Inbox size={36} />
          <p>
            {/* Un filtre posé change le sens d'une liste vide : « rien à traiter » annoncerait un
                parc sain alors qu'on ne regarde qu'une famille ou une gravité. */}
            {(filtreFamille || filtreSeverite || filtreCompte || filtrePlateforme)
              ? 'Aucune recommandation ne correspond aux filtres posés.'
              : vue === 'ignorees'
                ? 'Aucune recommandation traitée ou ignorée pour le moment.'
                : 'Rien à traiter : aucune recommandation ouverte sur vos comptes.'}
          </p>
        </div>
      ) : (
        <div className="radar-feed">
          {recommandations.map(reco => (
            <CarteRecommandation
              key={reco.id}
              reco={reco}
              vue={vue}
              depliee={Boolean(depliees[reco.id])}
              occupee={Boolean(enCours[reco.id])}
              onBasculer={() => setDepliees(p => ({ ...p, [reco.id]: !p[reco.id] }))}
              onDecider={decider}
              onReactiver={reactiver}
              onCorriger={(action, element) => setCorrection({ reco, action, element })}
              onRelancer={() => relancer(reco)}
              relanceEnCours={Boolean(relances[reco.id])}
              onFiltrerSeverite={basculerSeverite}
              onFiltrerFamille={basculerFamille}
              onFiltrerPlateforme={basculerPlateforme}
              formaterDate={formaterDate}
            />
          ))}
        </div>
      )}

      {/* Une seule modale pour tout le feed : la monter par carte en créerait deux cents, et
          deux cents écouteurs de clavier avec. */}
      {journalOuvert && (
        <JournalCorrections
          token={token}
          onFermer={() => setJournalOuvert(false)}
          // Une annulation remet le constat dans son état d'origine : la ligne doit revenir
          // dans le feed, donc on recharge.
          onAnnule={charger}
        />
      )}

      {correction && (
        <ModaleCorrection
          reco={correction.reco}
          action={correction.action}
          // Un élément précis a été désigné depuis la liste : la modale s'y limite.
          elementUnique={correction.element || null}
          token={token}
          onFermer={() => setCorrection(null)}
          onCorrige={corrige}
        />
      )}
    </div>
  );
};

/** La gravité, en clé courte : elle pilote le fond teinté de la carte en CSS. */
const CLE_GRAVITE = { CRITICAL: 'critique', WARNING: 'surveiller', INFO: 'info' };

/**
 * Une recommandation, en carte à fond teinté.
 *
 * ## Ce que la forme met en avant
 *
 * La **recommandation** est le titre : c'est le geste qu'on vient chercher, pas le nom du
 * défaut. Celui-ci passe en surtitre avec le compte et la gravité ; le constat chiffré vient
 * dessous, en une ligne.
 *
 * Le fond porte la gravité en aplat — elle se lit sans chercher un badge, et sans qu'un filet de
 * quelques pixels ait à la signaler. Un seul bouton est coloré : celui qui écrit dans le compte.
 * Les trois gestes déclaratifs — relancer, traiter, ignorer — passent en icônes, à gauche.
 *
 * ⚠️ **L'action est en fin de ligne**, là où le regard termine sa lecture et où l'on valide par
 * habitude. Les icônes à gauche ne se disputent pas cette place.
 */
const CarteRecommandation = ({ reco, vue, depliee, occupee, onBasculer, onDecider, onReactiver, onCorriger, onRelancer, relanceEnCours, onFiltrerSeverite, onFiltrerFamille, onFiltrerPlateforme, formaterDate }) => {
  const meta = LIBELLES_SEVERITE[reco.severity] || LIBELLES_SEVERITE.INFO;

  /*
   * Les colonnes du tableau des éléments concernés — déduites de la liste, pas déclarées par
   * contrôle : voir `colonnesElements.js`.
   */
  const elements = reco.affectedItems || [];
  const colonnes = colonnesDesElements(elements);
  const actionsDeLigne = reco.ecritureAutorisee ? (reco.actions || []) : [];
  // La règle vit dans `elementActionnable.js`, pas ici : recopiée, elle avait divergé — un
  // doublon de mot-clé n'offrait aucun bouton de ligne alors que la carte en annonçait un.
  const actionnable = elementActionnable;
  const colonneLien = elements.some(e => e.url);
  const colonneAction = elements.some(e => actionsDeLigne.some(a => actionnable(e, a)));

  const depliable = reco.affectedCount > 0 && elements.length > 0;
  const age = reco.rejoueLe ? null : ageDuConstat(reco.scannedAt);
  /*
   * ⚠️ **Un geste qui porte sur le constat n'a pas d'éléments à compter.** Les corrections de
   * flux produits visent le constat entier — les produits ne sont chargés qu'à l'ouverture du
   * geste. Filtrer sur `elementsActionnables > 0` les faisait disparaître sans rien signaler.
   */
  const corrections = (reco.actions || []).filter(a => a.surLeConstat || a.elementsActionnables > 0);

  return (
    <article className="radar-carte" data-gravite={CLE_GRAVITE[reco.severity] || 'info'}>
      <div className="radar-carte-haut">
        {LOGOS[reco.plateforme] && (
          <button
            type="button"
            className="radar-logo-bouton"
            onClick={() => onFiltrerPlateforme(reco.plateforme)}
            title={`Ne montrer que « ${libellePlateforme(reco.plateforme)} »`}
          >
            <img className="radar-logo" src={LOGOS[reco.plateforme]} alt="" />
          </button>
        )}

        <button
          type="button"
          className="radar-gravite"
          onClick={() => onFiltrerSeverite(reco.severity)}
          title={`Ne montrer que « ${meta.texte} »`}
        >
          <span className="radar-gravite-point" />
          {meta.texte}
        </button>

        <span className="radar-haut-sep">·</span>
        <span className="radar-compte" title={reco.compte.name}>{reco.compte.name}</span>

        <span className="radar-haut-sep">·</span>
        <span className="radar-controle">{reco.label}</span>

        {/* Le périmètre, quand un compte en porte plusieurs : deux cartes d'un même compte
            Merchant ne diffèrent sinon que par leur nombre de produits. */}
        {reco.scope && <><span className="radar-haut-sep">·</span><span>{reco.scope}</span></>}

        <button
          type="button"
          className="radar-famille"
          onClick={() => onFiltrerFamille(reco.family)}
          title={`Ne montrer que « ${familleConnue(reco.family).label} »`}
        >
          {familleConnue(reco.family).label}
        </button>

        {/* ⚠️ `rejoueLe` prime sur `scannedAt` : ce dernier vaut pour les cinquante-cinq
            contrôles du scan, et ferait passer pour vieux un constat rafraîchi à l'instant. */}
        {reco.rejoueLe ? (
          <span className="radar-frais" title="Ce constat vient d'être relevé — les autres contrôles de ce compte datent encore du scan.">
            <History size={12} />
            relevé à l'instant
          </span>
        ) : age && (
          <span className="radar-perime" title="Ce compte a pu changer depuis. Relancez le relevé de ce contrôle pour un état à jour.">
            <History size={12} />
            {age}
          </span>
        )}
      </div>

      {/* Le geste, en titre. Le nom du contrôle est resté au-dessus, en surtitre. */}
      <p className="radar-carte-reco">{reco.recommendation}</p>

      {reco.summary && <p className="radar-carte-constat">{reco.summary}</p>}

      <div className="radar-carte-bas">
        {vue === 'actives' ? (
          <>
            {/* Les gestes déclaratifs à gauche, l'écriture à droite : la fin de ligne est la
                position de validation, et c'est là que le regard termine sa lecture. */}
            <span className="radar-gestes-discrets">
              <button
                className="radar-geste radar-geste-icone"
                onClick={onRelancer}
                disabled={occupee}
                title="Relancer le relevé de ce contrôle — lecture seule, n'écrit rien. Compter quelques secondes."
                aria-label="Relancer le relevé"
              >
                <RefreshCw size={14} className={relanceEnCours ? 'radar-tourne' : ''} />
              </button>
              <button
                className="radar-geste radar-geste-icone"
                onClick={() => onDecider(reco, 'DONE')}
                disabled={occupee}
                title="Marquer traité"
                aria-label="Marquer traité"
              >
                <Check size={14} />
              </button>
              <button
                className="radar-geste radar-geste-icone"
                onClick={() => onDecider(reco, 'IGNORED')}
                disabled={occupee}
                title="Ignorer"
                aria-label="Ignorer"
              >
                <EyeOff size={14} />
              </button>
            </span>

            {depliable && (
              <button
                className="radar-geste"
                onClick={onBasculer}
                aria-expanded={depliee}
              >
                {depliee ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {depliee ? 'Masquer' : `Voir les ${reco.affectedCount} élément${reco.affectedCount > 1 ? 's' : ''}`}
              </button>
            )}

            {corrections.map(action => (
              reco.ecritureAutorisee ? (
                <button
                  key={action.cle}
                  className="radar-geste radar-geste-fort"
                  onClick={() => onCorriger(action)}
                  disabled={occupee}
                  title={action.description}
                >
                  <Wrench size={14} />
                  {action.libelle}
                  {action.ciblesDistinctes > 1 && ` (${action.ciblesDistinctes})`}
                </button>
              ) : (
                /* Compte non autorisé : le bouton se montre et explique, plutôt que de
                   disparaître — masquer laisserait croire que Kokpit ne sait pas corriger. */
                <button
                  key={action.cle}
                  className="radar-geste radar-geste-fort"
                  disabled
                  title={`${action.libelle} : l'écriture n'est pas activée sur ce compte. Un administrateur peut l'autoriser dans Admin › MCP.`}
                >
                  <Lock size={14} />
                  {action.libelle}
                </button>
              )
            ))}
          </>
        ) : (
          <>
            <span className="radar-trace">
              {/* « Corrigée » porte une preuve — l'écriture dans la régie —, « Traitée » est une
                  parole. Les afficher pareil perdrait la seule des deux qui soit vérifiable. */}
              {reco.decision?.decision === 'FIXED'
                ? 'Corrigée depuis Kokpit'
                : reco.decision?.decision === 'DONE' ? 'Traitée' : 'Ignorée'} par{' '}
              <strong>{reco.decision?.decidedByEmail}</strong>
              {reco.decision?.decidedAt && ` le ${formaterDate(reco.decision.decidedAt)}`}
            </span>
            <button className="radar-geste" onClick={() => onReactiver(reco)} disabled={occupee}>
              <RotateCcw size={14} />
              Remettre à traiter
            </button>
          </>
        )}
      </div>

      {depliee && elements.length > 0 && (
        <div className="radar-detail">
          <table className="radar-grille">
            <thead>
              <tr>
                {colonnes.map(colonne => (
                  <th
                    key={colonne.entete}
                    scope="col"
                    className={colonne.principale ? 'radar-colonne-principale' : undefined}
                  >
                    {colonne.entete}
                  </th>
                ))}
                {/* Un lien et un bouton sont des gestes, pas des données : ils n'ont pas
                    d'en-tête, seulement un nom accessible. */}
                {colonneLien && <th scope="col" className="radar-colonne-lien" aria-label="Ouvrir dans la régie" />}
                {colonneAction && <th scope="col" className="radar-colonne-action" aria-label="Corriger cet élément" />}
              </tr>
            </thead>
            <tbody>
              {elements.map((element, index) => (
                <tr key={index}>
                  {colonnes.map(colonne => {
                    const texte = colonne.lire(element);
                    return (
                      <td
                        key={colonne.entete}
                        className={[
                          colonne.principale ? 'radar-element-nom' : null,
                          colonne.multiligne ? 'radar-element-multiligne' : null
                        ].filter(Boolean).join(' ') || undefined}
                      >
                        <TexteAccentue texte={texte} />
                      </td>
                    );
                  })}

                  {colonneLien && (
                    <td className="radar-colonne-lien">
                      {element.url && (
                        <a href={element.url} target="_blank" rel="noopener noreferrer" title="Ouvrir dans Google Ads">
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </td>
                  )}

                  {/*
                    Agir sur **cette ligne** sans passer par la liste entière.

                    ⚠️ Pour un conflit de négatif, la cible est le **négatif**, pas le mot-clé
                    affiché : retirer celui de cette ligne peut en débloquer d'autres. L'aperçu le
                    dit avant d'écrire, et c'est le même aperçu que pour le lot — on ne
                    court-circuite pas la relecture sous prétexte qu'on ne touche qu'une ligne.
                  */}
                  {colonneAction && (
                    <td className="radar-colonne-action">
                      {actionsDeLigne.map(action => (
                        actionnable(element, action) ? (
                          <button
                            key={action.cle}
                            className="radar-element-action"
                            onClick={() => onCorriger(action, element)}
                            disabled={occupee}
                            title={`${action.libelleUnitaire || action.libelle} — seulement cet élément`}
                          >
                            <Wrench size={13} />
                            {action.libelleUnitaire || action.libelle}
                          </button>
                        ) : null
                      ))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Certaines sources ne gardent qu'un échantillon. Le taire ferait passer une liste
              volontairement courte pour une liste tronquée par erreur. */}
          {reco.affectedComplet === false && elements.length < reco.affectedCount && (
            <p className="radar-echantillon">
              {elements.length} des {reco.affectedCount} éléments — l'outil dédié donne la liste entière.
            </p>
          )}
        </div>
      )}
    </article>
  );
};

export default RadarView;
